import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Activity, Sparkles, AlertTriangle, Check } from 'lucide-react';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import { apiClient } from '@/lib/api';
import logger from '@/lib/logger';
import { toast } from 'sonner';

const STAGE_COLORS = {
    new_lead: 'bg-blue-500/15 text-blue-600',
    call_back: 'bg-violet-500/15 text-violet-600',
    warm_lead: 'bg-amber-500/15 text-amber-700',
    hot_lead: 'bg-orange-500/15 text-orange-700',
    enrolled: 'bg-emerald-500/15 text-emerald-700',
    rejected: 'bg-rose-500/15 text-rose-700',
    not_interested: 'bg-slate-500/15 text-slate-600',
    new_student: 'bg-blue-500/15 text-blue-600',
    in_progress: 'bg-amber-500/15 text-amber-700',
    follow_up: 'bg-violet-500/15 text-violet-600',
    interested: 'bg-amber-500/15 text-amber-700',
    pitched: 'bg-orange-500/15 text-orange-700',
    closed: 'bg-emerald-500/15 text-emerald-700',
};

const SCOPE_META = {
    sales: { label: 'Sales', noun: 'leads', stageKey: 'stage', courseKey: 'interested_course_name', valueKeys: ['sale_amount', 'estimated_value'] },
    cs:    { label: 'CS',    noun: 'students', stageKey: 'stage', courseKey: 'current_course_name', valueKeys: ['enrollment_amount'] },
    mentor:{ label: 'Mentor',noun: 'students', stageKey: 'mentor_stage', courseKey: 'current_course_name', valueKeys: ['enrollment_amount'] },
    bd:    { label: 'BD',    noun: 'students', stageKey: 'bd_stage', courseKey: 'current_course_name', valueKeys: ['enrollment_amount'] },
};

const fmtTime = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleTimeString('en-AE', { hour: '2-digit', minute: '2-digit' }); }
    catch { return ''; }
};

const fmtDate = (iso) => {
    if (!iso) return 'Never';
    try { return new Date(iso).toLocaleDateString('en-AE', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }); }
    catch { return ''; }
};

/**
 * "Activity Today" widget — bar chart of records touched today by each agent,
 * plus a stale-records nudge that surfaces agents with neglected pipeline items
 * (>48h since last touch in an active stage). Works across dashboards via the
 * `scope` prop: `sales` (leads/assigned_to), `cs`, `mentor`, `bd` (students).
 */
export const AgentActivityTodayWidget = ({ scope = 'sales', staleHours = 48 }) => {
    const meta = SCOPE_META[scope] || SCOPE_META.sales;

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [stale, setStale] = useState({ total_stale: 0, by_agent: [] });
    const [staleLoading, setStaleLoading] = useState(true);

    const [open, setOpen] = useState(false);
    const [selected, setSelected] = useState(null);
    const [drillMode, setDrillMode] = useState('today'); // 'today' | 'stale'
    const [drillLoading, setDrillLoading] = useState(false);
    const [drillItems, setDrillItems] = useState([]);
    const [acking, setAcking] = useState({}); // agent_id -> bool while in-flight

    const ackAgent = async (agent) => {
        if (!agent || acking[agent.agent_id]) return;
        setAcking(prev => ({ ...prev, [agent.agent_id]: true }));
        try {
            await apiClient.post('/activity/stale/ack', {
                scope, agent_id: agent.agent_id, hours: 48,
            });
            setStale(prev => ({
                ...prev,
                by_agent: prev.by_agent.filter(r => r.agent_id !== agent.agent_id),
                total_stale: Math.max(0, (prev.total_stale || 0) - (agent.stale || 0)),
            }));
            toast.success(`Snoozed ${agent.full_name} for 48h`);
        } catch (e) {
            logger.error('Ack failed:', e);
            toast.error('Failed to acknowledge — try again');
        } finally {
            setAcking(prev => { const n = { ...prev }; delete n[agent.agent_id]; return n; });
        }
    };

    useEffect(() => {
        let aborted = false;
        (async () => {
            try {
                setLoading(true);
                setStaleLoading(true);
                const [a, s] = await Promise.all([
                    apiClient.get(`/activity/today?scope=${scope}`),
                    apiClient.get(`/activity/stale?scope=${scope}&hours=${staleHours}`).catch(() => ({ data: { total_stale: 0, by_agent: [] } })),
                ]);
                if (!aborted) {
                    setRows(a.data || []);
                    setStale(s.data || { total_stale: 0, by_agent: [] });
                }
            } catch (e) {
                logger.error(`Activity today (${scope}) fetch failed:`, e);
                if (!aborted) toast.error(`Failed to load ${meta.label} activity`);
            } finally {
                if (!aborted) { setLoading(false); setStaleLoading(false); }
            }
        })();
        return () => { aborted = true; };
    }, [scope, staleHours, meta.label]);

    const data = useMemo(() => {
        // Backend returns desc by touched_today. In Recharts' vertical
        // layout, `data[0]` is plotted at the TOP of the y-axis, so
        // passing the array as-is renders most-active on top, least on
        // bottom — which is what we want.
        return rows
            .filter(r => r.touched_today > 0)
            .map(r => ({
                name: (r.full_name || '').split(' ')[0],
                full_name: r.full_name,
                agent_id: r.agent_id,
                touched: r.touched_today,
                team: r.team_name,
            }))
            .sort((a, b) => b.touched - a.touched);
    }, [rows]);

    const totalTouched = data.reduce((s, r) => s + r.touched, 0);
    const activeAgents = data.length;

    const openDrill = async (agent, mode = 'today') => {
        if (!agent) return;
        setSelected(agent);
        setDrillMode(mode);
        setOpen(true);
        setDrillLoading(true);
        try {
            const path = mode === 'stale'
                ? `/activity/stale/items?scope=${scope}&agent_id=${encodeURIComponent(agent.agent_id)}&hours=${staleHours}`
                : `/activity/today/items?scope=${scope}&agent_id=${encodeURIComponent(agent.agent_id)}`;
            const r = await apiClient.get(path);
            setDrillItems(r.data?.items || []);
        } catch (e) {
            logger.error('Activity drill failed:', e);
            toast.error('Failed to load activity details');
            setDrillItems([]);
        } finally {
            setDrillLoading(false);
        }
    };

    return (
        <>
            <Card data-testid={`activity-today-widget-${scope}`}>
                <CardHeader className="flex flex-row items-center justify-between gap-2">
                    <div>
                        <CardTitle className="flex items-center gap-2 text-base">
                            <Activity className="h-4 w-4 text-emerald-500" />
                            {meta.label} — Activity Today
                        </CardTitle>
                        <p className="text-xs text-muted-foreground mt-1">
                            Click any bar for today&apos;s drill-down. Click a stale row to see neglected {meta.noun}.
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <Badge variant="secondary" data-testid="activity-total-touched">
                            <Sparkles className="h-3 w-3 mr-1" />
                            {totalTouched} touches
                        </Badge>
                        <Badge variant="outline" data-testid="activity-active-agents">
                            {activeAgents} agents
                        </Badge>
                        {stale.total_stale > 0 && (
                            <Badge className="bg-amber-500/15 text-amber-700 border-amber-500/30" data-testid="activity-stale-total">
                                <AlertTriangle className="h-3 w-3 mr-1" />
                                {stale.total_stale} stale &gt;{staleHours}h
                            </Badge>
                        )}
                    </div>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                        {/* Bar chart: 2/3 width */}
                        <div className="lg:col-span-2">
                            {loading ? (
                                <div className="space-y-2">
                                    {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-7 w-full" />)}
                                </div>
                            ) : data.length === 0 ? (
                                <div className="text-center py-10 text-sm text-muted-foreground">
                                    No agent activity recorded today yet.
                                </div>
                            ) : (
                                <ResponsiveContainer width="100%" height={Math.max(220, data.length * 28)}>
                                    <BarChart
                                        data={data}
                                        layout="vertical"
                                        margin={{ top: 4, right: 24, bottom: 4, left: 8 }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                                        <XAxis type="number" allowDecimals={false} stroke="hsl(var(--muted-foreground))" fontSize={11} />
                                        <YAxis
                                            type="category"
                                            dataKey="name"
                                            width={100}
                                            stroke="hsl(var(--muted-foreground))"
                                            fontSize={11}
                                        />
                                        <Tooltip
                                            cursor={{ fill: 'hsl(var(--muted))', opacity: 0.4 }}
                                            contentStyle={{ background: 'hsl(var(--popover))', border: '1px solid hsl(var(--border))', borderRadius: 8, fontSize: 12 }}
                                            formatter={(v) => [`${v} ${meta.noun}`, 'Touched today']}
                                            labelFormatter={(_, payload) => (payload && payload[0]) ? `${payload[0].payload.full_name}${payload[0].payload.team ? ' — ' + payload[0].payload.team : ''}` : ''}
                                        />
                                        <Bar
                                            dataKey="touched"
                                            radius={[0, 6, 6, 0]}
                                            onClick={(p) => openDrill(p, 'today')}
                                            cursor="pointer"
                                            data-testid="activity-bar"
                                        >
                                            {data.map((d, i) => {
                                                // data is desc-sorted: index 0 = #1, < 3 = top-3.
                                                const fill = i === 0 ? '#10b981' : i < 3 ? '#3b82f6' : '#8b5cf6';
                                                return (
                                                    <Cell
                                                        key={d.agent_id}
                                                        fill={fill}
                                                        data-testid={`activity-bar-${d.agent_id}`}
                                                    />
                                                );
                                            })}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            )}
                        </div>

                        {/* Stale leaderboard: 1/3 width */}
                        <div className="border-l lg:pl-4">
                            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1.5">
                                <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                                Stale ({staleHours}h+ untouched)
                            </div>
                            {staleLoading ? (
                                <div className="space-y-1.5">
                                    {[1, 2, 3].map(i => <Skeleton key={i} className="h-7 w-full" />)}
                                </div>
                            ) : stale.by_agent.length === 0 ? (
                                <div className="text-xs text-muted-foreground py-4 text-center">
                                    Everyone is on top of their {meta.noun}.
                                </div>
                            ) : (
                                <ul className="space-y-1" data-testid="stale-list">
                                    {stale.by_agent.map(s => (
                                        <li key={s.agent_id} className="flex items-stretch gap-1">
                                            <button
                                                onClick={() => openDrill(s, 'stale')}
                                                className="flex-1 flex items-center justify-between gap-2 px-2 py-1.5 rounded-md text-xs hover:bg-amber-500/10 transition-colors text-left group"
                                                data-testid={`stale-row-${s.agent_id}`}
                                            >
                                                <span className="truncate">
                                                    <span className="font-medium">{s.full_name}</span>
                                                    {s.team_name && <span className="text-muted-foreground"> · {s.team_name}</span>}
                                                </span>
                                                <Badge className="bg-amber-500/15 text-amber-700 group-hover:bg-amber-500/25 tabular-nums">
                                                    {s.stale}
                                                </Badge>
                                            </button>
                                            <button
                                                onClick={(e) => { e.stopPropagation(); ackAgent(s); }}
                                                title="Acknowledge & snooze for 48h"
                                                className="px-1.5 rounded-md text-emerald-600 hover:bg-emerald-500/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                                                disabled={acking[s.agent_id]}
                                                data-testid={`stale-ack-${s.agent_id}`}
                                            >
                                                <Check className="h-3.5 w-3.5" />
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                </CardContent>
            </Card>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col" data-testid="activity-drilldown-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            {drillMode === 'stale' ? (
                                <><AlertTriangle className="h-4 w-4 text-amber-500" />
                                Stale {meta.noun} — {selected?.full_name}</>
                            ) : (
                                <><Activity className="h-4 w-4 text-emerald-500" />
                                {meta.noun.charAt(0).toUpperCase() + meta.noun.slice(1)} touched today — {selected?.full_name}</>
                            )}
                            <Badge variant="secondary" className="ml-2" data-testid="activity-drilldown-count">{drillItems.length}</Badge>
                        </DialogTitle>
                    </DialogHeader>
                    <div className="overflow-auto flex-1 -mx-1">
                        {drillLoading ? (
                            <div className="space-y-2 p-2">
                                {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-10 w-full" />)}
                            </div>
                        ) : drillItems.length === 0 ? (
                            <div className="text-center py-10 text-sm text-muted-foreground">No records found.</div>
                        ) : (
                            <table className="w-full text-sm">
                                <thead className="sticky top-0 bg-background border-b text-xs uppercase text-muted-foreground">
                                    <tr>
                                        <th className="text-left p-2">{meta.label === 'Sales' ? 'Lead' : 'Student'}</th>
                                        <th className="text-left p-2">Stage</th>
                                        <th className="text-left p-2">Course</th>
                                        <th className="text-right p-2">Value</th>
                                        <th className="text-right p-2">{drillMode === 'stale' ? 'Last touch' : 'Touched at'}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {drillItems.map(l => {
                                        const stage = l[meta.stageKey];
                                        const value = meta.valueKeys.map(k => l[k]).find(v => v) || '';
                                        return (
                                            <tr key={l.id} className="border-b last:border-b-0 hover:bg-muted/40" data-testid={`activity-item-${l.id}`}>
                                                <td className="p-2">
                                                    <div className="font-medium">{l.full_name}</div>
                                                    <div className="text-xs text-muted-foreground font-mono">{l.phone}</div>
                                                </td>
                                                <td className="p-2">
                                                    <span className={`px-2 py-0.5 rounded text-[11px] font-medium ${STAGE_COLORS[stage] || 'bg-muted text-muted-foreground'}`}>
                                                        {(stage || '—').replace(/_/g, ' ')}
                                                    </span>
                                                </td>
                                                <td className="p-2 text-xs">{l[meta.courseKey] || '—'}</td>
                                                <td className="p-2 text-right font-mono text-xs">{value}</td>
                                                <td className="p-2 text-right text-xs text-muted-foreground">
                                                    {drillMode === 'stale' ? fmtDate(l.last_activity) : fmtTime(l.last_activity)}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default AgentActivityTodayWidget;

 